import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/motion";
import { PrimaryCTA } from "@/components/CTA";

const TIERS = [
  {
    name: "Avvio Smart",
    desc: "Analisi rapida di mercato e storico, ottimizzazione tariffaria e pricing.",
    ideal: "Il punto di partenza.",
  },
  {
    name: "Boost",
    desc: "Revenue management continuativo: monitoraggio della domanda e prezzo dinamico.",
    ideal: "Il prezzo giusto, tutto l'anno.",
  },
  {
    name: "Pro",
    desc: "Gestione commerciale: revenue, distribuzione e canali di vendita.",
    ideal: "Deleghi tutta la parte commerciale.",
    featured: true,
  },
  {
    name: "Premium",
    desc: "Gestione completa, dalla A alla Z: prezzo, distribuzione, prenotazioni e ospiti.",
    ideal: "La struttura gira, tu segui solo i risultati.",
  },
  {
    name: "Signature",
    desc: "Soluzioni su misura per portafogli o progetti dedicati.",
    ideal: "Formule costruite sul tuo caso.",
  },
];

export const Livelli = () => (
  <section id="servizi" data-testid="section-servizi" className="bg-cream py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-3xl">
        <motion.p variants={fadeUp} className="mb-5 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
          I livelli di servizio
        </motion.p>
        <motion.h2 variants={fadeUp} className="font-display text-4xl font-medium tracking-tight text-navy sm:text-5xl">
          Un livello di gestione per ogni esigenza.
        </motion.h2>
        <motion.p variants={fadeUp} className="mt-7 text-base leading-relaxed text-text-muted sm:text-lg">
          Dalla singola ottimizzazione tariffaria alla gestione completa, fino all'avvio di una nuova
          struttura. Si parte sempre da un'analisi: le condizioni si definiscono insieme, sul tuo caso
          reale.
        </motion.p>
      </motion.div>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5"
      >
        {TIERS.map((t, i) => (
          <motion.article
            key={t.name}
            variants={fadeUp}
            data-testid={`tier-${i}`}
            className={`group relative flex flex-col rounded-2xl border p-7 transition-transform duration-500 hover:-translate-y-2 ${
              t.featured
                ? "border-gold bg-navy text-white shadow-[0_20px_50px_rgba(31,58,95,0.25)] lg:-mt-4 lg:mb-4"
                : "border-navy/10 bg-white text-navy-deep shadow-[0_8px_30px_rgba(31,58,95,0.05)]"
            }`}
          >
            {t.featured && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-navy-deep">
                Consigliato
              </span>
            )}
            <span className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${t.featured ? "text-gold" : "text-gold"}`}>
              Livello {String(i + 1).padStart(2, "0")}
            </span>
            <h3 className={`mt-3 font-display text-2xl font-semibold ${t.featured ? "text-white" : "text-navy"}`}>
              {t.name}
            </h3>
            <p className={`mt-4 flex-1 text-sm leading-relaxed ${t.featured ? "text-white/75" : "text-text-muted"}`}>
              {t.desc}
            </p>
            <div className={`mt-5 flex items-start gap-2 border-t pt-5 text-sm ${t.featured ? "border-white/15 text-white/90" : "border-navy/10 text-navy"}`}>
              <Check className={`mt-0.5 h-4 w-4 shrink-0 ${t.featured ? "text-gold" : "text-amber"}`} />
              <span>{t.ideal}</span>
            </div>
          </motion.article>
        ))}
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className="mt-14 flex flex-col items-center gap-6 text-center"
      >
        <p className="max-w-2xl text-base leading-relaxed text-text-muted sm:text-lg">
          Ogni collaborazione parte da un'analisi gratuita del tuo immobile. Le condizioni si
          costruiscono su misura — parliamone.
        </p>
        <PrimaryCTA testId="servizi-cta">Scopri quale livello fa per te</PrimaryCTA>
      </motion.div>
    </div>
  </section>
);
